import { addDays, differenceInHours } from 'date-fns';
import { formatDateTime } from './format-date';

/**
 * Get the abandonment reminder text for a payment/subscription/booking
 * @param {Object} seller - The Seller object with checkoutAbandonment settings
 * @param {boolean} [seller.checkoutAbandonment.sendEmails] - Whether abandonment emails are enabled
 * @param {Object} item - The payment, subscription, or booking object
 * @param {string} item.abandonedCartEmailStatus - Email status (pending, first_sent, second_sent, third_sent)
 * @param {Date|string} item.abandonedCartEmailSentAt - When the last email was sent
 * @param {Date|string} item.createdAt - When the item was created
 * @param {Date|string} item.recoveredAt - When the item was recovered (stops reminders)
 * @returns {string} The formatted reminder text
 */
export default function getAbandonmentReminderText(seller, item) {
  const createdAt = new Date(item.createdAt);
  const now = new Date();
  const sentDate = formatDateTime(item.abandonedCartEmailSentAt);
  const isRecovered = item.recoveredAt != null;
  const sendEmailsDisabled = seller?.checkoutAbandonment?.sendEmails !== true;

  if (!item.abandonedCartEmailSentAt && item.abandonedCartEmailStatus === 'skipped') {
    return 'Reminders stopped';
  }

  if (item.abandonedCartEmailSentAt && item.abandonedCartEmailStatus === 'skipped') {
    return `Sent (${sentDate}). Reminders stopped`;
  }

  // Early return for disabled emails - no need to calculate timing
  if (sendEmailsDisabled && !isRecovered) {
    if (item.abandonedCartEmailStatus === 'first_sent') {
      return `1st sent (${sentDate}). Reminders stopped`;
    }
    if (item.abandonedCartEmailStatus === 'second_sent') {
      return `2nd sent (${sentDate}). Reminders stopped`;
    }
  }

  if (item.abandonedCartEmailStatus === 'first_sent') {
    if (isRecovered) {
      return `1st sent (${sentDate}). Reminders stopped`;
    }

    const secondReminderTime = addDays(createdAt, 1);
    const hoursUntilNext = differenceInHours(secondReminderTime, now);

    if (hoursUntilNext > 0) {
      return `1st sent (${sentDate}). Next email in ${hoursUntilNext} hours`;
    }
    return `1st sent (${sentDate}). 2nd reminder pending`;
  }

  if (item.abandonedCartEmailStatus === 'second_sent') {
    if (isRecovered) {
      return `2nd sent (${sentDate}). Reminders stopped`;
    }

    const thirdReminderTime = addDays(createdAt, 2);
    const hoursUntilNext = differenceInHours(thirdReminderTime, now);

    if (hoursUntilNext > 0) {
      return `2nd sent (${sentDate}). Next email in ${hoursUntilNext} hours`;
    }
    return `2nd sent (${sentDate}). 3rd reminder pending`;
  }

  if (item.abandonedCartEmailStatus === 'third_sent') {
    return `3rd sent (${sentDate}). Reminders stopped`;
  }

  return sentDate;
}
